import Box from "@mui/material/Box";
import {flexRender, type RowData, type Table as TanStackTable} from "@tanstack/react-table";
import type {KeyboardEvent, MouseEvent} from "react";
import {useTranslation} from "react-i18next";
import ServerTableEmptyState from "./components/ServerTableEmptyState";
import ServerTableSkeleton from "./ServerTableSkeleton";
import type {ServerTableEmptyStateType, ServerTablePresentation} from "./types";
import {getColumnAlignment, getPinnedColumnSx} from "./utils";

const INTERACTIVE_TARGET_SELECTOR = [
    'a',
    'button',
    'input',
    'label',
    'select',
    'summary',
    'textarea',
    '[contenteditable="true"]',
    '[data-no-row-click]',
    '[role="button"]',
    '[role="checkbox"]',
    '[role="combobox"]',
    '[role="link"]',
    '[role="menuitem"]',
    '[role="option"]',
    '[role="radio"]',
    '[role="slider"]',
    '[role="spinbutton"]',
    '[role="switch"]',
    '[role="tab"]',
    '[role="textbox"]',
    '[tabindex]:not([tabindex="-1"])'
].join(',');

const isInteractiveRowTarget = (
    target: EventTarget,
    currentTarget: HTMLTableRowElement
) => {
    if (!(target instanceof Element)) return false;

    const interactiveTarget = target.closest(INTERACTIVE_TARGET_SELECTOR);
    return interactiveTarget !== null
        && interactiveTarget !== currentTarget
        && currentTarget.contains(interactiveTarget);
};

interface ServerTableBodyProps<TData extends RowData> {
    table: TanStackTable<TData>
    isLoading?: boolean
    isFetching?: boolean
    isError?: boolean
    initialContentRevealed: boolean
    columnCount: number
    rowMinHeight: number
    rowHoverColor: string
    emptyState: ServerTableEmptyStateType
    presentation?: ServerTablePresentation
    onRowClick?: (row: TData) => void
}

export function ServerTableBody<TData extends RowData>({
    table,
    isLoading,
    isFetching,
    isError,
    initialContentRevealed,
    columnCount,
    rowMinHeight,
    rowHoverColor,
    emptyState,
    presentation,
    onRowClick
}: ServerTableBodyProps<TData>) {
    const {t} = useTranslation();
    const rows = table.getRowModel().rows;
    const showError = !isLoading && isError;
    const showRows = !isLoading && !isError && rows.length > 0;
    const showEmptyState = !isLoading && !isError && rows.length === 0;

    // Loading rows stay still; resolved cells share the table's one-time reveal.
    const contentVisible = isLoading || initialContentRevealed;

    return (
        <Box
            component="tbody"
            sx={{
                opacity: isFetching ? 0.5 : 1,
                transition: "opacity 180ms ease",
                '& > tr': {height: rowMinHeight},
                '& > tr > td': {
                    borderBottom: "1px solid #f0f0f0",
                    verticalAlign: "middle",
                    opacity: contentVisible ? 1 : 0,
                    transition: isLoading
                        ? "none"
                        : "opacity 180ms ease-out"
                },
                '@media (prefers-reduced-motion: reduce)': {
                    transition: "none",
                    '& > tr > td': {
                        transition: "none"
                    }
                }
            }}
        >
            {isLoading && (
                <ServerTableSkeleton table={table}/>
            )}

            {showError && (
                <Box component="tr">
                    <Box component="td" colSpan={columnCount} sx={{borderBottom: "none"}}>
                        {t('Could not load rows.')}
                    </Box>
                </Box>
            )}

            {showRows && rows.map(row => {
                const cells = row.getVisibleCells();

                const handleRowClick = (event: MouseEvent<HTMLTableRowElement>) => {
                    if (isInteractiveRowTarget(event.target, event.currentTarget)) return;

                    // Dragging to select text in a cell (e.g. copying an id) still ends in a `click` on the
                    // row, because the mousedown and mouseup share the <tr>. Don't navigate when a text
                    // selection is active: a genuine click collapses any selection on mousedown so it stays
                    // collapsed here, whereas a drag-select leaves it non-collapsed. (=== false so a
                    // null selection defaults to navigating rather than blocking the click.)
                    if (window.getSelection()?.isCollapsed === false) return;

                    onRowClick?.(row.original);
                };
                const handleRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
                    // Interactive descendants own their keyboard behavior; only the focused row activates itself.
                    if (event.target !== event.currentTarget) return;
                    if (event.key !== "Enter" && event.key !== " ") return;

                    event.preventDefault();
                    onRowClick?.(row.original);
                };

                return (
                    <Box
                        component="tr"
                        key={row.id}
                        tabIndex={onRowClick ? 0 : undefined}
                        onClick={onRowClick ? handleRowClick : undefined}
                        onKeyDown={onRowClick ? handleRowKeyDown : undefined}
                        sx={{
                            cursor: onRowClick ? "pointer" : "default",
                            '&:focus-visible > td': {
                                outline: '2px solid',
                                outlineColor: 'primary.main',
                                outlineOffset: -2
                            },
                            '&:hover > td': {
                                backgroundColor: "background.paper",
                                backgroundImage: `linear-gradient(${rowHoverColor}, ${rowHoverColor})`
                            }
                        }}
                    >
                        {cells.map(cell => (
                            <Box
                                component="td"
                                key={cell.id}
                                sx={{
                                    px: 1,
                                    textAlign: getColumnAlignment(cell.column),
                                    ...getPinnedColumnSx(cell.column, 1)
                                }}
                            >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </Box>
                        ))}

                    </Box>
                );
            })}

            {showEmptyState && (
                <ServerTableEmptyState
                    columnCount={columnCount}
                    type={emptyState}
                    presentation={presentation}
                />
            )}
        </Box>
    );
}

export default ServerTableBody;
